import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

const GFC = (
  <a href="https://glenfallscabin.com" target="_blank" rel="noopener noreferrer" className="text-[#2D4A3E] underline underline-offset-2 hover:text-[#1e3329] transition-colors font-medium">
    Glen Falls Luxury Cabin
  </a>
);

interface Tip {
  icon: string;
  title: string;
  desc: ReactNode;
  link?: string;
  linkLabel?: string;
}

const TIPS: Tip[] = [
  {
    icon: 'ri-map-pin-2-line',
    title: 'Downtown vs. Secluded',
    desc: <>If walkability matters — being able to stroll to dinner or browse Main Street — look for properties close to downtown Highlands. Old Edwards Inn is the most central option. If you prefer privacy and a true mountain feel, a cabin in the surrounding forest is the better choice. {GFC} offers a rare middle ground: secluded setting, short drive to town.</>,
  },
  {
    icon: 'ri-home-4-line',
    title: 'Cabin vs. Hotel',
    desc: 'Cabins give you more space, a full kitchen, outdoor living areas, and privacy. Hotels offer concierge service, on-site dining, and daily housekeeping. For longer stays or family trips, cabins usually win. For a short visit where you want everything handled for you, a hotel like Old Edwards Inn or Highlander Mountain House makes sense.',
  },
  {
    icon: 'ri-footprint-line',
    title: 'Pet Friendly Options',
    desc: <>{GFC}, Stag Ridge, and Lark Lodge are among the pet-friendly places to stay in Highlands, NC. Most hotels in the area do not accept pets. Always confirm the pet policy and any fees before booking.</>,
    link: '/pet-friendly-cabins-highlands-nc',
    linkLabel: 'Pet Friendly Cabin Guide',
  },
  {
    icon: 'ri-walk-line',
    title: 'Proximity to Hiking and Waterfalls',
    desc: <>If outdoor access is a priority, look for properties along Falls Drive or near the Nantahala National Forest. {GFC} is within walking distance of the Glen Falls trailhead. Falls Drive Cottage is also well-positioned for waterfall access. Most other Highlands lodging requires a short drive to reach the main trails.</>,
    link: '/cabins-near-glen-falls-highlands-nc',
    linkLabel: 'Cabins Near Glen Falls',
  },
  {
    icon: 'ri-group-line',
    title: 'Family vs. Couples Trips',
    desc: <>For couples, a smaller cabin or boutique hotel room works well. For families, look for properties with multiple bedrooms, a full kitchen, and outdoor space. {GFC} (sleeps 6), Stag Ridge (sleeps 6), and Laurelwood are all solid family options. Old Edwards Inn also has family-friendly suites if you prefer a hotel.</>,
  },
];

export default function PTSHowToChooseSection() {
  return (
    <section className="bg-[#FAFAF8] py-24 px-6 lg:px-14">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-[#C9A96E] text-xs tracking-[0.3em] uppercase font-jost mb-4">/ Decision Guide</p>
          <h2 className="font-cormorant text-5xl text-[#1A1A18] font-light">
            How to Choose Where to Stay<br /><em className="italic">in Highlands, NC</em>
          </h2>
          <p className="text-[#5C5850] font-jost text-sm leading-relaxed max-w-xl mx-auto mt-6">
            The right place depends on your priorities. Here&apos;s how to think through the decision.
          </p>
        </div>

        <div className="space-y-8">
          {TIPS.map((tip, idx) => (
            <div key={tip.title} className="flex items-start gap-6">
              <div className="shrink-0 flex flex-col items-center">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#EDE8E0]">
                  <i className={`${tip.icon} text-[#2D4A3E] text-base`} />
                </div>
                {idx < TIPS.length - 1 && (
                  <div className="w-px flex-1 bg-[#D4CFC7] mt-3 min-h-[32px]" />
                )}
              </div>
              <div className="pb-8">
                <h3 className="font-cormorant text-2xl text-[#1A1A18] font-medium mb-2">{tip.title}</h3>
                <p className="text-[#5C5850] font-jost text-sm leading-relaxed mb-3">{tip.desc}</p>
                {tip.link && (
                  <Link
                    to={tip.link}
                    className="inline-flex items-center gap-1.5 text-[#2D4A3E] text-xs font-jost font-medium underline underline-offset-2 hover:text-[#1e3329] transition-colors cursor-pointer"
                  >
                    {tip.linkLabel}
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs" />
                    </span>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
